import React, {
    forwardRef,
    Ref,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { makeStyles } from "@mui/styles";
import {
    Backdrop,
    Box,
    Fade,
    IconButton,
    Modal,
    Typography,
} from "@mui/material";
import styles from "./ProgressLevelModal.module.scss";
import Close from "@mui/icons-material/Close";
import ProgressLevel from "../ProgressLevel/ProgressLevel";
import { LevelAchievementFormatted } from "../ProgressLevel/ProgressLevel.action";
import { ProgressLevelData } from "../OlympianCardV2";
import ProgressLevelMobile from "../ProgressLevelMobile";

interface ProgressLevelModalProps {
    levels: LevelAchievementFormatted[];
    gameUserLevel: ProgressLevelData | null;
}
export interface ProgressLevelModalRef {
    setIsVisible: (visible: boolean) => void;
    isVisible: boolean;
}
export interface ITime {
    hour: number;
    minute: number;
}

const useStyles = makeStyles({
    modal: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        "@media (max-width: 600px)": {
            marginTop: "0px",
        },
    },
});

const ProgressLevelModal = (
    { levels, gameUserLevel }: ProgressLevelModalProps,
    ref: Ref<ProgressLevelModalRef>
) => {
    const isDesktop = window.innerWidth >= 768;
    const classes = useStyles();
    const [isVisible, setIsVisible] = useState(false);
    const [completedLevels, setCompletedLevels] = useState<number>(0);

    const [time, setTime] = useState<ITime>({
        hour: 0,
        minute: 0,
    });

    const handleClose = () => {
        setIsVisible(false);
    };

    useEffect(() => {
        const convertSecondsToHourMinute = (seconds: number) => {
            const hour = Math.floor(seconds / 3600);
            const minute = Math.floor((seconds % 3600) / 60);
            return { hour, minute };
        };
        if (gameUserLevel) {
            const { hour, minute } = convertSecondsToHourMinute(
                gameUserLevel?.value
            );
            setTime({ hour, minute });
        }
    }, [gameUserLevel]);

    useImperativeHandle(
        ref,
        () => ({
            isVisible,
            setIsVisible,
        }),
        [isVisible]
    );

    useEffect(() => {
        if (levels?.length > 0 && gameUserLevel) {
            const completedLevel = levels.findIndex(
                (level) => level.id === gameUserLevel.metadata.current.id
            );

            setCompletedLevels(completedLevel);
        }
    }, [levels, gameUserLevel]);

    return (
        <Modal
            open={isVisible}
            onClose={handleClose}
            className={classes.modal}
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={isVisible}>
                <div className={styles["progress_level-modal"]}>
                    <div className={styles["progress_level-header"]}>
                        <IconButton
                            onClick={handleClose}
                            className="close-button"
                        >
                            <Close
                                sx={{
                                    color: "#666666",
                                    fontSize: "25px",
                                }}
                            />
                        </IconButton>
                    </div>
                    {isDesktop ? (
                        <div className={styles["progress_level-content"]}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: {
                                        xs: "0px",
                                        md: "0 0 0 30px",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100px",
                                        height: "100px",
                                    }}
                                >
                                    <img
                                        src={`${
                                            gameUserLevel?.metadata?.current?.iconUrl.split(
                                                ".png"
                                            )[0]
                                        }_128x128.png`}
                                        alt="Aviron Level Achievement"
                                        width={"100%"}
                                        height={"100%"}
                                        style={{
                                            objectFit: "contain",
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: "GRIFTER",
                                            fontWeight: 700,
                                            fontSize: "24px",
                                            marginBottom: "16px",
                                        }}
                                    >
                                        {gameUserLevel?.metadata?.current?.name?.toUpperCase()}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <img
                                            alt="icon-time"
                                            src="/images/icon-time.png"
                                            style={{
                                                width: "21px",
                                                height: "18px",
                                                objectFit: "contain",
                                                marginRight: "8px",
                                            }}
                                        />
                                        {time.hour > 0 && (
                                            <Typography
                                                sx={{
                                                    fontFamily: "MonoSpec",
                                                    fontWeight: 500,
                                                    fontSize: "16px",
                                                    marginRight: "8px",
                                                }}
                                            >
                                                {time.hour}{" "}
                                                {time.hour > 1
                                                    ? "HOURS"
                                                    : "HOUR"}{" "}
                                            </Typography>
                                        )}
                                        <Typography
                                            sx={{
                                                fontFamily: "MonoSpec",
                                                fontWeight: 500,
                                                fontSize: "16px",
                                            }}
                                        >
                                            {time.minute}{" "}
                                            {time.minute > 1
                                                ? "MINUTES"
                                                : "MINUTE"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <ProgressLevel
                                levels={levels}
                                gameUserLevel={gameUserLevel}
                                completedLevels={completedLevels}
                            />
                        </div>
                    ) : (
                        <ProgressLevelMobile
                            levels={levels}
                            gameUserLevel={gameUserLevel}
                            time={time}
                            completedLevels={completedLevels}
                        />
                    )}
                </div>
            </Fade>
        </Modal>
    );
};

export default forwardRef(ProgressLevelModal);
